// Builds the prop object passed into a chart component (via DynamicChart scope).
//
// ONE entry point: mapRowsToComponentProps — real /data rows → component props.
//
// There used to be a second, `sampleScope`, which manufactured a plausible
// business dataset per chart family so the builder's canvas was never empty.
// That is fabricated data on a runtime path, and rendered through the real
// component with the real theme it was indistinguishable from a measured chart,
// so it is gone (AG-2101). A preview with no rows shows the SCHEMA
// (`SchemaPreview`) or an empty state; nothing in this file invents a value.
//
// Charts are pure-SVG components keyed on conventional props (xKey/yKey,
// category/value, nodes/links, ...). We pick keys from the dragged axis fields
// where possible and fall back to the component's defaults.

import { NEUTRAL_SERIES, NEUTRAL_STATUS, type StatusRole } from '../../../styles/dataTokens';
import { roleSizePx } from '../dashboard/typography';
import type { DashTheme } from '../dashboard/types';
import { fieldLeaf, humanizeField } from './fieldLabels';
import { fmt } from './formatValue';
import type { AxisConfig, ChartOverlay, PropDef } from './types';

/** The largest period-over-period percentage a KPI will print.
 *
 *  `code_generator.py` already states the rule for an authored page — "a percent
 *  change against a near-zero base (or past ~300%) is shown as the ABSOLUTE
 *  change instead — a four-digit percent is a bug the reader sees". This is that
 *  same number, enforced where the props are actually built, so the rule holds
 *  for a fallback-rendered KPI and for a spec that authored its own figure. */
const MAX_DELTA_PCT = 300;

// Reduce a measure's raw values to the ONE scalar a single-value chart type
// (kpi/metric, gauge/radial/bullet, and any future one) needs, honoring the
// axis's own `aggregation` — same vocabulary the backend's `_aggregate_values`
// uses (proxy.py), so a chart-type's scalar reduction never silently disagrees
// with what the axis config actually declared.
//
// Backend aggregation only ever groups rows BY A DIMENSION AXIS (aggregate_rows
// bails out early when the chart has none — true for every single-value chart
// type) — so whatever the axis's aggregation means has to be honored HERE,
// against however many raw rows the source actually returned. Most
// single-value sources naturally return exactly one row, where every
// aggregation trivially agrees; a source that legitimately returns many rows
// for one measure (e.g. a webhook buffer of live-pushed readings, oldest row
// first) is where the choice of aggregation actually matters — `sum` adds a
// stream of live readings into a meaningless total, while `actual`/unset
// (this codebase's "no aggregation requested" value, see proxy.py's
// `_NO_AGG`) means the latest one.
function reduceToScalar(nums: number[], aggregation: string | undefined): number {
  if (!nums.length) return 0;
  const agg = String(aggregation || '').toLowerCase();
  switch (agg) {
    case 'sum': return nums.reduce((a, b) => a + b, 0);
    case 'avg':
    case 'average': return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min': return Math.min(...nums);
    case 'max': return Math.max(...nums);
    // The rows ARE counts. The server aggregates before they leave: an
    // undimensioned scalar tile arrives as the one count it computed
    // (proxy.reduce_to_scalar), and a KPI given its time axis (D6) arrives as
    // one count per period. Counting the rows answered "how many buckets" —
    // 1 for a scalar, the number of months for a series — so a count KPI over a
    // dated source would have read 6 for six months of orders. The total of a
    // count series is the sum of its buckets, and a single row is itself.
    case 'count': return nums.reduce((a, b) => a + b, 0);
    case 'median': {
      const sorted = [...nums].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    }
    default: return nums[nums.length - 1]; // 'actual' / unset: latest reading
  }
}

// Canvas typography/style derived from the chart properties. Applied to the
// wrapper around a rendered chart; SVG <text> inherits these via the CSS
// cascade, so font family/size/colour/weight affect every label on the chart.
export function canvasStyle(properties: Record<string, unknown>): Record<string, string | number> {
  const s: Record<string, string | number> = {};
  // THE BOARD'S TYPEFACE, NOT THIS FILE'S. This used to assign the Inter stack
  // unconditionally before reading `properties.fontFamily`, and SVG <text>
  // inherits the canvas font through the cascade — so every chart whose tile was
  // not handed an explicit font drew its labels in Inter, on a board whose theme
  // named Fraunces or Space Grotesk. The renderer's own default disagreed with
  // the board it was drawing on.
  //
  // `--dt-font` is the board's font, published by `themeVars` on the dashboard
  // root; outside a board it resolves to the app's own `--ff-ui`. An explicit
  // `properties.fontFamily` still wins, and that is the path `_apply_theme_to_charts`
  // uses to carry the board font into the sandboxed share frame, which has no
  // app stylesheet for a variable to resolve against.
  s.fontFamily = 'var(--dt-font, var(--ff-ui, ui-sans-serif, system-ui, sans-serif))';
  const ff = properties.fontFamily;
  if (ff && String(ff).trim()) s.fontFamily = String(ff);
  const fs = properties.fontSize;
  if (fs != null && fs !== '') s.fontSize = Number(fs);
  const dark = properties.theme === 'dark';
  // Apply a canvas background ONLY when explicitly set, so a standalone dark chart
  // (agent sets backgroundColor) reads dark, while a dashboard-themed tile (bg left
  // empty) stays transparent and shows the themed card surface behind it.
  const bg = properties.backgroundColor as string;
  if (bg && String(bg).trim()) {
    s.background = String(bg);
    s.borderRadius = 12;
  }
  // A dark board with no stated text colour falls to the app's own inverse
  // foreground rather than to a hex chosen here.
  const tc = properties.textColor || (dark ? 'var(--fg-inverse, #e8edf7)' : '');
  if (tc && String(tc).trim()) s.color = String(tc);
  const fw = properties.fontWeight;
  if (fw && String(fw).trim()) s.fontWeight = String(fw);
  return s;
}

/** The type scale a chart SEED draws its own SVG text at (AG-2334).
 *
 *  `canvasStyle` above sets CSS `font-size` on the wrapper, which an SVG
 *  `<text fontSize={N}>` PRESENTATION ATTRIBUTE does not inherit — that gap is
 *  exactly why `chart_seeds.py` grew 82 literal px values, each disconnected
 *  from the board's actual density/type scale. These three map onto the same
 *  measurement-driven roles the rest of the board already uses
 *  (`roleSizePx` — `legibility`'s `rootFontPx`/`densityTypeScale`), so a seed
 *  reads a computed size instead of inventing its own: `tickFontPx` for small
 *  axis/legend/caption text, `valueFontPx` for a bold data-value/callout
 *  label, `bigFigurePx` for the one big centred figure a chart form has
 *  (donut/pie centre total, a KPI-style headline number).
 */
export function chartTypeScaleProps(properties: Record<string, unknown>): Record<string, number> {
  const theme: DashTheme = {
    density: properties.density as DashTheme['density'],
    typeScale: properties.typeScale as DashTheme['typeScale'],
  };
  return {
    tickFontPx: roleSizePx('caption', theme),
    valueFontPx: roleSizePx('data', theme),
    bigFigurePx: roleSizePx('metric', theme),
  };
}

/** Whether an axis carries TIME — the only axis a period-over-period comparison
 *  can be measured along.
 *
 *  The backend used to own this rule, in `session._kpi_measurement`, whose own
 *  comment named this file as the looser twin: "that one sparks any multi-row
 *  KPI, so a count broken down by STATUS would spark across categories and
 *  report the gap between two of them as a change over time". That measurement
 *  existed to inline a figure into a generated page; nothing is inlined now, so
 *  it is gone and this is the surviving implementation — the rule came with it
 *  rather than being dropped alongside it (AG-2102).
 *
 *  Both halves of the test the backend applied: the DECLARED type, and the
 *  aggregation, since a measure bucketed by month is a period series whatever
 *  its field is called. */
export function isTemporalAxis(axis?: { type?: string; aggregation?: string }): boolean {
  if (!axis) return false;
  if (/date|time/i.test(String(axis.type || ''))) return true;
  return ['year', 'quarter', 'month', 'week', 'day', 'hour']
    .includes(String(axis.aggregation || '').toLowerCase());
}


/** The colour this board gives a semantic role, or the app's own.
 *
 *  A peak, a low and an anomaly are STATUS, not decoration, and a board that
 *  published `color_roles` has already said what its green is. These used to be
 *  three raw hexes here, which made this module a fourth opinion on the question
 *  and meant an overlay marker could disagree with the KPI beside it (AG-2106).
 */
function roleColor(properties: Record<string, unknown>, role: StatusRole): string {
  const roles = properties.statusColors as Partial<Record<StatusRole, string>> | undefined;
  return (roles && roles[role]) || NEUTRAL_STATUS[role];
}

/**
 * The value a bound field takes in one returned row, or `undefined` (AG-2114).
 *
 * A row keys a bound field FOUR ways, and this mapper only ever tried one of
 * them — the axis slot. A flattened source delivers `items[].data.age_days` as
 * `age_days`, so a KPI bound to it resolved in NO row, and the count fallback
 * below then answered with the number of rows: a confident **3** on a board of
 * 1,074 issues, printed in 48px as though it were a fact.
 *
 * The backend has had the four-way resolver the whole time
 * (`binding_validation.row_value`) and this is its mirror, in the same order:
 * the field path, the axis slot, the field's flattened last segment, the axis
 * label. Each lookup is tried only when that spelling was supplied.
 */
export function rowValue(
  row: Row, field?: string, slot?: string, label?: string,
): unknown {
  for (const key of [field, slot]) {
    if (key && key in row) return row[key];
  }
  if (field) {
    const leaf = String(field).replace(/\[\]\./g, '.').split('.').pop();
    if (leaf && leaf in row) return row[leaf];
  }
  if (label && label in row) return row[label];
  return undefined;
}

/**
 * The numbers a measure axis actually resolves to across these rows.
 *
 * Separated from "did it resolve at all", because those are different questions
 * and conflating them is the whole of AG-2114: a measure that resolves nowhere
 * is not a measure of zero, and it is not a count of rows either.
 */
function measureValues(rows: Row[], axis: AxisConfig | undefined, slot: string): number[] {
  const out: number[] = [];
  for (const row of rows) {
    const raw = rowValue(row, axis?.field, slot, axis?.label);
    const n = Number(raw);
    if (raw !== undefined && raw !== null && raw !== '' && !isNaN(n)) out.push(n);
  }
  return out;
}

function seriesColorsOf(properties: Record<string, unknown>): Record<string, string> {
  return (properties.seriesColors as Record<string, string>) || {};
}

function defaultsFrom(props: Record<string, PropDef> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, def] of Object.entries(props || {})) {
    if (def && 'default' in def) out[k] = def.default;
  }
  return out;
}

type Row = Record<string, unknown>;

function buildTree(rows: Row[]): Record<string, unknown> {
  // rows: {source: parent, target: child} → nested {name, children}
  const children: Record<string, string[]> = {};
  const asChild = new Set<string>();
  for (const r of rows) {
    const p = String(r.source ?? ''); const c = String(r.target ?? '');
    if (!p || !c) continue;
    (children[p] ||= []).push(c); asChild.add(c);
  }
  const roots = Object.keys(children).filter((n) => !asChild.has(n));
  const rootName = roots[0] || Object.keys(children)[0] || 'root';
  const seen = new Set<string>();
  const node = (name: string): Record<string, unknown> => {
    if (seen.has(name)) return { name };
    seen.add(name);
    const kids = (children[name] || []).map(node);
    return kids.length ? { name, children: kids } : { name };
  };
  return node(rootName);
}

// #4 SMART TRENDS — compute trend-analysis overlays from a time-ordered series
// (opt-in via a `trend` property the agent sets when the user asks for trends).
// Emits overlays the seeds already render: a moving-average LINE, and PEAK / LOW /
// ANOMALY markers. Works on any ordered numeric series; shines on a real
// time-series. Anomalies = points >2σ from the moving average.
function trendOverlays(rows: Row[], yKey: string, properties: Record<string, unknown>): ChartOverlay[] {
  const flag = String(properties.trend ?? (properties.showTrend ? 'auto' : '')).toLowerCase();
  const on = flag === 'auto' || flag === 'true' || flag === 'movingaverage' || properties.trend === true || properties.showTrend === true;
  if (!on) return [];
  const ys = rows.map((r) => Number((r as Row)[yKey])).map((n) => (isNaN(n) ? 0 : n));
  const n = ys.length;
  if (n < 5) return [];
  const win = Math.max(2, Math.min(Number(properties.trendWindow) || 7, Math.floor(n / 2)));
  const ma = ys.map((_, i) => {
    const a = Math.max(0, i - Math.floor(win / 2)); const b = Math.min(n, i + Math.ceil(win / 2));
    const s = ys.slice(a, b); return s.reduce((p, v) => p + v, 0) / s.length;
  });
  const muted = (properties.muted as string) || NEUTRAL_STATUS.neutral;
  const out: ChartOverlay[] = [{ kind: 'line', points: ma.map((v) => ({ value: v })), color: muted, label: `${win}-pt avg` } as unknown as ChartOverlay];
  let hi = 0; let lo = 0;
  ys.forEach((v, i) => { if (v > ys[hi]) hi = i; if (v < ys[lo]) lo = i; });
  out.push({ kind: 'marker', index: hi, value: ys[hi], label: 'Peak', color: roleColor(properties, 'positive') } as unknown as ChartOverlay);
  out.push({ kind: 'marker', index: lo, value: ys[lo], label: 'Low', color: roleColor(properties, 'negative') } as unknown as ChartOverlay);
  const res = ys.map((v, i) => v - ma[i]);
  const mean = res.reduce((p, v) => p + v, 0) / n;
  const sd = Math.sqrt(res.reduce((p, v) => p + (v - mean) ** 2, 0) / n) || 1;
  ys.map((v, i) => ({ i, z: Math.abs((v - ma[i]) / sd) }))
    .filter((a) => a.z > 2 && a.i !== hi && a.i !== lo)
    .sort((a, b) => b.z - a.z).slice(0, 3)
    .forEach((a) => out.push({ kind: 'marker', index: a.i, value: ys[a.i], label: 'Anomaly', color: roleColor(properties, 'warning') } as unknown as ChartOverlay));
  return out;
}

// BENCHMARKS — a reference rule or a band, drawn only when the tile supplies one.
//
// The seeds have rendered `kind: 'reference'` (a dashed rule with a label) and
// `kind: 'band'` (a shaded range) for as long as they have existed, and NOTHING
// ever emitted either: the only producer was `trendOverlays`, which emits a
// moving-average line and markers. So a reference board's "2024 Avg Engagement:
// 79.49M" rule, its "Max Optimal Value" threshold and its target lines had a
// renderer at both ends and no middle (POWERBI_GAP_ANALYSIS_2026-09-11.md,
// Gap 9 — AG-1881).
//
// Opt-in, exactly like `trend`, and generic in what it accepts:
//   benchmark: 250                      → a rule at 250
//   benchmark: { value, label }         → a rule with the caller's own caption
//   benchmark: { lower, upper, label }  → a band between two values
//   benchmark: 'average' | 'avg' | 'mean' → a rule at the series' own mean
//
// A number is a TARGET someone decided; 'average' is DERIVED from the rows in
// hand. Nothing here inspects a field name or a chart type, and a tile that
// supplies no benchmark gets no rule — a chart must never assert a threshold
// nobody set.
function benchmarkOverlays(rows: Row[], yKey: string, properties: Record<string, unknown>): ChartOverlay[] {
  const raw = properties.benchmark ?? properties.reference ?? properties.target;
  if (raw === undefined || raw === null || raw === '') return [];
  const muted = (properties.muted as string) || NEUTRAL_STATUS.neutral;
  const nums = rows.map((r) => Number((r as Row)[yKey])).filter((n) => !isNaN(n));

  const spec = (typeof raw === 'object') ? raw as Record<string, unknown> : { value: raw };
  const label = typeof spec.label === 'string' ? spec.label : '';
  const color = typeof spec.color === 'string' ? spec.color : muted;

  const lower = Number(spec.lower); const upper = Number(spec.upper);
  if (isFinite(lower) && isFinite(upper)) {
    return [{ kind: 'band', lower, upper, label, color } as unknown as ChartOverlay];
  }

  let value = Number(spec.value);
  if (!isFinite(value)) {
    const named = String(spec.value ?? '').toLowerCase();
    if (!nums.length) return [];
    if (named === 'average' || named === 'avg' || named === 'mean') {
      value = nums.reduce((a, b) => a + b, 0) / nums.length;
    } else if (named === 'median') {
      const sorted = [...nums].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      value = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    } else if (named === 'max') {
      value = Math.max(...nums);
    } else if (named === 'min') {
      value = Math.min(...nums);
    } else {
      return [];   // an instruction nobody here understands draws nothing
    }
  }
  if (!isFinite(value)) return [];
  return [{ kind: 'reference', value, label: label || fmt.compact(value), color } as unknown as ChartOverlay];
}

// LAST-RESORT palette — used ONLY when the design layer supplied no palette (no
// dashboard theme and no explicit `colors`). It is deliberately a plain, neutral,
// distinct-hue set whose only job is to keep multi-series / multi-category charts
// legible; it is NOT a house style. The REAL palette comes from the AI's design
// spec (dashboard theme → props.colors), which overrides this everywhere.
//
// IT LIVES IN THE TOKEN LAYER NOW (AG-2106). It used to be eight Tableau-ish
// hues written down here, in the renderer — which made this module a second
// design system underneath the dashboard theme, and one that DISAGREED with the
// backend's answer to the same question (`design_generator._heuristic` renders
// an undesigned board in the Okabe-Ito accessibility ramp, precisely so an
// undesigned board does not look designed). One question, one answer:
// `styles/dataTokens.NEUTRAL_SERIES`.
//
// It is the FLOOR under every data mark (DASHBOARD_DESIGN_AUDIT.md D1). Every seed
// ends `colorList || [color] || ['currentColor']`, and `currentColor` is whatever
// text colour the tile happens to inherit — so a chart the mapper handed no
// colours to painted a five-slice donut as one near-black ring. No branch below
// may return `colors: undefined` or `color: ''` for a data mark: a part-to-whole
// chart gets the whole palette, a single-hue mark gets its first entry.
export const FALLBACK_PALETTE: string[] = [...NEUTRAL_SERIES];

/**
 * AN OVERLAY DRAWS IN THE BOARD'S OWN INK (AG-2327).
 *
 * `analytics.py` used to supply four saturated hues (a rose, a sky, a violet
 * and a slate) whenever the author stated none, and the comment on the first of
 * them was the bug report: it said the rose "stands out against the indigo
 * default bars". There are no indigo default bars any more — that palette went
 * with AG-2101..2113 — so the four were a second palette chosen against a board
 * that no longer exists, painted over whatever palette the design did author.
 *
 * ⚠️ The hexes are deliberately NOT quoted here. `noSecondDesignSystem.test.ts`
 * forbids a hue anywhere in this module and is right to: a hue in a comment is
 * one paste away from being a hue in the code.
 *
 * ⚠️ WHY HERE AND NOT IN THE SEED. The seeds draw an overlay as
 * `stroke={o.color}` — an SVG PRESENTATION ATTRIBUTE, where `var(--dt-accent)`
 * does not resolve. Handing the seed a CSS variable would make every overlay
 * invisible rather than themed. This module already resolves every other colour
 * a chart draws with, and it holds the concrete values, so it is where a missing
 * one gets filled.
 *
 * An author's own colour is never touched.
 */
export function overlayColours(
  overlays: unknown,
  accent: string,
  palette: string[] | null,
): unknown {
  if (!Array.isArray(overlays) || !overlays.length) return overlays;
  const pal = palette && palette.length ? palette : FALLBACK_PALETTE;
  // The emphasis hue for the thing the overlay is ASSERTING (a trend, a
  // forecast), and a quieter one for the reference marks a reader checks
  // against rather than reads. Both come off the board's own palette.
  const asserting = accent || pal[0];
  const quiet = pal.length > 1 ? pal[pal.length - 1] : asserting;
  return overlays.map((o) => {
    if (!o || typeof o !== 'object') return o;
    const ov = o as Record<string, unknown>;
    if (typeof ov.color === 'string' && ov.color.trim()) return ov;   // authored: untouched
    return { ...ov, color: ov.kind === 'line' ? asserting : quiet };
  });
}

/** The palette the design layer chose, else the floor. `properties.colors` is
 *  what `_apply_theme_to_charts` stamps; an explicit single `color` is a caller's
 *  own choice and wins for the single-hue seeds. */
function paletteOf(properties: Record<string, unknown>): string[] {
  return Array.isArray(properties.colors) && (properties.colors as string[]).length
    ? (properties.colors as string[]) : FALLBACK_PALETTE;
}

function accentOf(properties: Record<string, unknown>): string {
  return (properties.color as string) || paletteOf(properties)[0];
}

// Real data → props for the component. Proxy rows are keyed by the named axis
// keys (x/y/category/value/source/target/open/high/low/close/size/...).
export function mapRowsToComponentProps(
  chartType: string,
  rows: Row[],
  fieldMap: Record<string, AxisConfig>,
  properties: Record<string, unknown>,
  componentProps?: Record<string, PropDef>,
  overlays?: ChartOverlay[],
): Record<string, unknown> {
  // `overlays` is passed through into scope so chart components that declare an
  // `overlays` prop can draw trend/reference/band layers; components that don't
  // simply ignore it (no per-type code — the Qdrant component invariant holds).
  const base = {
    ...defaultsFrom(componentProps), ...properties,
    // Renderer-computed, never author-configurable — always wins over
    // anything stray in `properties` (see chartTypeScaleProps).
    ...chartTypeScaleProps(properties),
    data: rows, overlays: overlays ?? [],
  };
  const t = chartType;

  if (t === 'kpi' || t === 'metric') {
    // One headline number. Value = the mapped measure aggregated across rows (the
    // pipeline already aggregated per its axis config; summing collapses a scalar
    // or totals a trend series). A date/dimension on `x` yields the sparkline.
    const keys = Object.keys(fieldMap || {});
    const dimKeys = ['x', 'category', 'label', 'region'];
    const measureKey = ('value' in (fieldMap || {})) ? 'value'
      : ('y' in (fieldMap || {})) ? 'y'
        : keys.find((k) => !dimKeys.includes(k));
    // A metric with NO numeric measure axis (e.g. "interviews by date" = a bare
    // date dimension) is a COUNT of records — falling back to keys[0] (the date)
    // and Number()-ing it yields NaN → 0, which is why such KPIs showed 0.
    const valKey = measureKey || keys[0] || 'value';
    // Order rows chronologically when the x-axis is a date, so the sparkline and
    // its start/end captions read oldest→newest (the API often returns newest
    // first, which produced a reversed "22 Jul — 16 Jul" range).
    // A COMPARISON NEEDS A TIME AXIS TO BE A COMPARISON (AG-2102).
    //
    // `isDateX` used to be `'x' in fieldMap` — the mere PRESENCE of an x
    // binding. A count broken down by status is several rows on an x axis of
    // categories, so the sparkline ran across categories and the "delta"
    // reported the gap between the last two of them as a change over time. That
    // is a trend nobody measured, printed under a caption that says "vs prev
    // period".
    //
    // Ordering still keys off the same axis (sorting categories by Date() was
    // always a no-op, since the parse fails and the comparator returns 0), but
    // the SERIES and everything derived from it now require a real time axis.
    const isDateX = isTemporalAxis(fieldMap?.x as AxisConfig | undefined);
    const orderedRows = isDateX
      ? [...rows].sort((a, b) => {
          const da = new Date(String((a as Row).x)).getTime();
          const db = new Date(String((b as Row).x)).getTime();
          return (isNaN(da) || isNaN(db)) ? 0 : da - db;
        })
      : rows;
    const nums = measureValues(orderedRows, fieldMap?.[valKey], valKey);
    // AN UNRESOLVABLE MEASURE DECLINES; IT DOES NOT COUNT ROWS (AG-2114).
    //
    // These are two different states and the code below used to treat them as
    // one, because `measureKey && nums.length` is false in both:
    //
    //   * NO MEASURE AXIS AT ALL — "interviews by date" binds a date and nothing
    //     else, so the tile IS a count of records and `rows.length` is the right
    //     answer. This is legitimate and stays.
    //   * A MEASURE AXIS THAT RESOLVES NOWHERE — the binding exists but no row
    //     carries it. `rows.length` then answers a different question: over a
    //     grouped count it is "how many categories are there", which rendered a
    //     confident 3 for a board of 1,074 issues.
    //
    // The second is an absence, and an absence is reported as one. `null` reaches
    // the seed as an em-dash: "this number is not available" is true, and a
    // plausible wrong number says something false that the reader cannot check.
    const countsRecords = !measureKey;
    const unresolved = Boolean(measureKey) && nums.length === 0 && rows.length > 0;
    const explicitValue = (properties.value != null && properties.value !== '') ? properties.value : undefined;
    // A RATE computed by the data layer wins outright: `_rate` is the real
    // numerator/denominator division (completion %, breach rate) that no
    // single binding can express — see proxy.py's rate_condition support.
    const rate = (properties as { _rate?: { pct?: number; numerator?: number; denominator?: number } })._rate;
    // Counting fallback: prefer the snapshot's TRUE pre-cap total when it
    // rode along (rows may be capped for payload; the count must not be).
    const rowsTotal = Number((properties as Record<string, unknown>).rows_total) || 0;
    const value = (rate && typeof rate.pct === 'number') ? rate.pct
      : explicitValue
      ?? (nums.length ? reduceToScalar(nums, fieldMap?.[valKey]?.aggregation)
        : unresolved ? null
          : countsRecords ? (rowsTotal > orderedRows.length ? rowsTotal : orderedRows.length)
            : 0);
    // No number, no series and no comparison: every one of them would be derived
    // from the same rows the measure could not be found in.
    const spark = (isDateX && !unresolved && nums.length > 1) ? nums : [];
    // Delta = LATEST bucket vs the bucket BEFORE it. Every caption on a KPI card
    // says "vs prev period", and this used to be last-vs-FIRST — a full-range
    // change wearing a period-over-period label. When the series is bucketed by
    // the board's time grain, last-vs-previous is the true prev-period figure;
    // a two-point series degenerates to the same number either way.
    const delta = spark.length >= 2 ? spark[spark.length - 1] - spark[spark.length - 2] : null;
    // Trend % vs the previous bucket — feeds the delta pill when the seed shows
    // one. Explicit properties win. Guard a near-zero baseline (which otherwise
    // yields a nonsense % like 10000%): require a meaningful base and clamp.
    const base0 = Math.abs(spark[spark.length - 2] ?? 0);
    const rawPct = (delta !== null && base0 > 1e-6) ? (delta / base0) * 100 : null;
    // THE CEILING APPLIES TO AN AUTHORED FIGURE TOO.
    //
    // It used to guard only the computed branch, and `properties.deltaPct` was
    // taken ahead of it untouched — so a spec that authored one printed it
    // whatever it said. A live board's headline KPI read "+2881% vs prev period"
    // against the near-empty leading edge of its own series (AG-2032). A
    // four-digit percentage is a bug the reader sees, and it does not become
    // true by having been written down upstream.
    const credible = (pct: number | null | undefined): number | null =>
      (typeof pct === 'number' && isFinite(pct) && Math.abs(pct) <= MAX_DELTA_PCT)
        ? Math.round(pct * 10) / 10
        : null;
    const deltaPct = credible(properties.deltaPct as number | undefined) ?? credible(rawPct);
    // A date/period on the x slot yields the sparkline's start/end captions.
    const xVals = isDateX ? orderedRows.map((r) => (r as Row).x).filter((v) => v != null) : [];
    const fmtPeriod = (v: unknown) => {
      const s = String(v);
      const d = new Date(s);
      return !isNaN(d.getTime()) && /\d{4}/.test(s) ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : s.slice(0, 12);
    };
    const sparkStart = (properties.sparkStart as string) ?? (xVals.length > 1 ? fmtPeriod(xVals[0]) : '');
    const sparkEnd = (properties.sparkEnd as string) ?? (xVals.length > 1 ? fmtPeriod(xVals[xVals.length - 1]) : '');
    const label = (properties.kpiLabel as string) || fieldMap?.[valKey]?.label || (properties.title as string) || 'Value';
    const accent = accentOf(properties);
    // Pass the label under BOTH keys so a generated card (reads kpiLabel) and the
    // seed (reads label) both show the metric name instead of a generic default.
    const out: Record<string, unknown> = { ...base, value, label, kpiLabel: label, title: label, spark, delta, deltaPct, sparkStart, sparkEnd, valueKey: valKey, color: accent };
    if (rate && typeof rate.pct === 'number') {
      out.format = 'percent';
      // The division shown as itself — "41 of 500" under the percentage keeps
      // the rate auditable, the way the reference states its evidence.
      out.subLabel = `${Number(rate.numerator ?? 0).toLocaleString()} of ${Number(rate.denominator ?? 0).toLocaleString()}`;
      out.spark = []; out.delta = null; out.deltaPct = null; // series math doesn't apply to a served rate
    }
    return out;
  }

  if (t === 'goal_progress') {
    // Value = the mapped measure aggregated across rows (same reduction as kpi).
    // Target: an explicit `target` axis wins; else an explicit `target` property;
    // else a "just above value" fallback so the ring/bar never renders empty.
    const keys = Object.keys(fieldMap || {});
    const valKey = ('value' in (fieldMap || {})) ? 'value'
      : ('y' in (fieldMap || {})) ? 'y'
        : (keys.find((k) => k !== 'target') || keys[0] || 'value');
    // Resolved four ways, like every other bound measure (AG-2114).
    const nums = measureValues(rows, fieldMap?.[valKey], valKey);
    const value = reduceToScalar(nums, fieldMap?.[valKey]?.aggregation);
    const tgtNums = ('target' in (fieldMap || {}))
      ? measureValues(rows, fieldMap?.target, 'target') : [];
    const target = tgtNums.length ? reduceToScalar(tgtNums, fieldMap?.target?.aggregation)
      : (isFinite(Number(properties.target)) && Number(properties.target) > 0 ? Number(properties.target) : (value > 0 ? value * 1.25 : 100));
    const label = (properties.kpiLabel as string) || fieldMap?.[valKey]?.label || (properties.title as string) || 'Goal progress';
    const accent = accentOf(properties);
    return { ...base, value, target, label, color: accent };
  }

  if (t === 'gauge' || t === 'radial' || t === 'bullet') {
    // Gauge/radial/bullet want a single scalar `value` (like KPI) plus a `max` for
    // the arc. Without this branch they'd fall through to cartesian props and get
    // no `value` → render an empty 0 arc. Aggregate the mapped measure to a scalar.
    const keys = Object.keys(fieldMap || {});
    const valKey = ('value' in (fieldMap || {})) ? 'value'
      : ('y' in (fieldMap || {})) ? 'y'
        : (keys.find((k) => !['x', 'category', 'label', 'region'].includes(k)) || keys[0] || 'value');
    const nums = measureValues(rows, fieldMap?.[valKey], valKey);
    const value = reduceToScalar(nums, fieldMap?.[valKey]?.aggregation);
    // `max`: explicit property wins; else a "nice" round number just above the
    // value so the arc reads as a meaningful fill (never 0/full by accident).
    const propMax = Number(properties.max);
    const niceMax = (x: number) => {
      if (!(x > 0)) return 100;
      const p = Math.pow(10, Math.floor(Math.log10(x)));
      const n = x / p;
      return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
    };
    const max = (isFinite(propMax) && propMax > 0) ? propMax : niceMax(value * 1.2);
    const min = isFinite(Number(properties.min)) ? Number(properties.min) : 0;
    const label = (properties.kpiLabel as string) || fieldMap?.[valKey]?.label || (properties.title as string) || '';
    const color = accentOf(properties);
    return { ...base, value, min, max, label, color };
  }

  if (t === 'status_tile' || t === 'status') {
    // One card per entity. `labelKey` = the entity dimension; `metrics` = every
    // other mapped column (each becomes a threshold-coloured bar). Rows are keyed
    // by the axis names, so metric keys read straight off the rows.
    const keys = Object.keys(fieldMap || {});
    const labelKey = ['category', 'label', 'x', 'name', 'region', 'entity'].find((k) => k in (fieldMap || {})) || keys[0] || 'category';
    const metricKeys = keys.filter((k) => k !== labelKey);
    const metrics = (metricKeys.length ? metricKeys : keys.filter((k) => k !== labelKey))
      .map((k) => ({ key: k, label: fieldMap?.[k]?.label || k }));
    return { ...base, labelKey, metrics, colors: paletteOf(properties) };
  }

  if (t === 'segmented_progress') {
    // One proportional bar: category → labelKey, value → valueKey. Per-category
    // colors follow the same precedence as pie/donut (seriesColors → palette).
    const fromSeries = seriesColorsOf(properties);
    const data = rows.map((r) => {
      const cat = String((r as Row).category ?? '');
      const c = fromSeries[cat];
      return c ? { ...r, color: c } : r;
    });
    return { ...base, data, labelKey: 'category', valueKey: 'value', colors: paletteOf(properties), title: (properties.title as string) || '' };
  }

  if (t === 'insight_banner') {
    // Not data-bound — the sentence is authored (by the planner/agent or the
    // user), not derived from rows. `data` stays as the raw rows only for
    // reference. Prop is `message`, NOT `text` — `text` is the design-theme
    // system's generic foreground-color token and gets stamped onto every
    // chart's properties by the theming step, which would silently clobber
    // an authored sentence stored under that same key.
    return { ...base, message: (properties.message as string) || '', tone: (properties.tone as string) || 'neutral', icon: (properties.icon as string) || '' };
  }

  if (t === 'ranked_list') {
    /*
     * Leaderboard: the seed reads `name`, and the ROW still has to arrive as it
     * is.
     *
     * This used to RENAME the key — `{name: r.category, value, delta}` — and
     * that broke every model-authored leaderboard on a live board, which is
     * where it was found. A generated component is written against the row
     * shape it was SHOWN, and that shape is the axis names (`category`,
     * `value`): so it read `d.category`, got undefined, and rendered a column
     * of bars with no product names beside them. One tile went further and
     * printed "No matching products for the current filters" under a chart
     * whose query had returned thirty rows, because its own derived list came
     * out empty.
     *
     * So the keys are ADDED, never renamed. The seed keeps reading `name`
     * through `nameKey`, and code written against the row keeps working —
     * neither has to know about the other.
     */
    const hasDelta = 'y2' in (fieldMap || {});
    const data = rows.map((r) => {
      const row = r as Row;
      return { ...row, name: row.category ?? row.name ?? row.x, value: row.value, delta: row.y2 };
    });
    return { ...base, data, nameKey: 'name', valueKey: 'value', deltaKey: hasDelta ? 'delta' : '', colors: paletteOf(properties) };
  }

  if (t === 'table' || t === 'data_table') {
    // Label columns with the tile's REAL field names (from the binding), not the
    // generic slot keys (x/y/y2) — so headers read "Jd / Views", not "X / Y".
    // fieldMap entries may be {field,label} OR a bare field-path string; either way
    // take the field's leaf and humanize it.
    //
    // The humanizing used to be `replace(/\b\w/g, m => m.toUpperCase())`, which
    // only capitalises the FIRST letter of a camelCase name because `\b` matches
    // nothing inside it. `priceChangePercent` came out "PriceChangePercent", and
    // the header's `textTransform: uppercase` finished it as PRICECHANGEPERCENT
    // on a live board. Splitting now lives in fieldLabels.ts, the twin of the
    // backend's labels.py.
    const fm = (fieldMap || {}) as Record<string, unknown>;
    const labelFor = (k: string): string => {
      const e = fm[k] as { label?: string; field?: string } | string | undefined;
      if (e && typeof e === 'object') return e.label || humanizeField(e.field || k);
      return humanizeField((e as string) || k);
    };
    const keys = Object.keys(fm).length ? Object.keys(fm)
      : Object.keys((rows[0] as Row) || {}).filter((k) => !k.startsWith('_'));
    // A record table's ROWS are keyed by the field's leaf, not by the plot slot
    // — the server renames them on the way out so a register's headers read
    // "Created / Story Points" instead of "X / Y". The columns were still built
    // from the SLOT names, so every column whose slot name differed from its
    // leaf asked the row for a key it does not have and rendered blank.
    //
    // That is not an edge case: the two slots that always differ are the
    // dimension (`x`/`category`) and the primary measure (`y`/`value`) — the
    // two columns a table exists for. Live on 2026-09-11, a nine-column table
    // showed values in one column; SYMBOL and PRICECHANGEPERCENT were empty in
    // every row.
    //
    // Ask the rows which key they carry rather than assuming either convention:
    // the server skips the rename when two bindings share a leaf, so both
    // shapes are real and a fixed guess would be wrong half the time.
    const first = (rows[0] as Row) || {};
    const rowKeyFor = (slot: string): string => {
      const e = fm[slot] as { field?: string } | string | undefined;
      const leaf = fieldLeaf(typeof e === 'object' && e ? (e.field || slot) : ((e as string) || slot));
      if (leaf && leaf in first) return leaf;
      return slot;
    };
    const columns = keys.map((k) => ({ key: rowKeyFor(k), label: labelFor(k) }));
    return { ...base, columns };
  }

  if (['pie', 'donut', 'treemap', 'sunburst', 'funnel'].includes(t)) {
    // Per-slice palette: an explicit `colors` palette wins, else any colours the
    // user set per category (seriesColors), else the FLOOR. This branch used to
    // return `undefined` here, with a comment claiming the seeds had "a built-in
    // multi-hue categorical palette". No seed has one — their last resort is
    // `currentColor` — which is how a five-product donut rendered as one solid
    // near-black ring with five identical legend dots (D1). A part-to-whole chart
    // whose parts are indistinguishable conveys nothing, so it always has one.
    const explicit = Array.isArray(properties.colors) && (properties.colors as string[]).length ? (properties.colors as string[]) : null;
    const fromSeries = Object.values(seriesColorsOf(properties));
    const colors = explicit ?? (fromSeries.length ? fromSeries : FALLBACK_PALETTE);
    return { ...base, category: 'category', value: 'value', colors, innerRadius: t === 'donut' ? (properties.innerRadius ?? 60) : 0 };
  }
  // Sequential/map seeds shade a SINGLE hue by value — they read `color`, not the
  // `colors` palette array. Fold the theme's first palette colour into `color` so a
  // themed dashboard tints these to match; with no theme the floor's first hue,
  // never '' (which the seeds resolve to inherited text colour).
  const seqColor = accentOf(properties);
  if (['scatter', 'bubble', 'hexbin'].includes(t))
    return { ...base, xKey: 'x', yKey: 'y', sizeKey: 'size', color: seqColor };
  if (t === 'heatmap')
    return { ...base, xKey: 'x', yKey: 'y', valueKey: 'value', color: seqColor };
  if (t === 'stock_volume')
    return { ...base, xKey: 'x', volumeKey: 'volume' };
  if (t === 'candlestick' || t === 'ohlc')
    return { ...base, xKey: 'x' }; // rows already carry open/high/low/close
  if (t === 'choropleth')
    return { ...base, regionKey: 'region', valueKey: 'value', color: seqColor };
  if (t === 'symbol_map')
    return { ...base, latKey: 'lat', lonKey: 'lon', valueKey: 'value', labelKey: 'region', color: seqColor };
  if (t === 'sankey')
    return { ...base, data: { links: rows.map((r) => ({ source: String(r.source), target: String(r.target), value: Number(r.value) || 1 })) } };
  if (t === 'flow_map')
    return { ...base, data: { points: [...new Set(rows.flatMap((r) => [String(r.source), String(r.target)]))].map((n) => ({ name: n, lat: 0, lon: 0 })), flows: rows.map((r) => ({ from: String(r.source), to: String(r.target), value: Number(r.value) || 1 })) } };
  if (t === 'network') {
    const nodes = [...new Set(rows.flatMap((r) => [String(r.source), String(r.target)]).filter(Boolean))].map((id) => ({ id }));
    return { ...base, data: { nodes, links: rows.map((r) => ({ source: String(r.source), target: String(r.target) })) } };
  }
  if (t === 'dendrogram' || t === 'org_chart')
    return { ...base, data: buildTree(rows) };
  if (t === 'flowchart')
    return { ...base, data: rows.map((r) => ({ label: String(r.x ?? '') })) };

  const seriesColors = (properties.seriesColors as Record<string, string>) || {};

  // Multi-series charts: the proxy PIVOTS the breakdown dimension into wide rows
  // (one column per distinct breakdown value). The series are therefore the row
  // columns other than the primary dimension key — derive them from the data so
  // the component draws one bar/area per breakdown value. (With no breakdown
  // mapped the proxy returns a single measure column, so this yields one series.)
  // Resolve the DIMENSION and MEASURE keys generically. Axis keys are not always
  // the canonical x/y — the agent (or a manual mapping) may name them after the
  // field (e.g. {name, market_cap_rank}) or use category/value. Proxy rows are
  // keyed by whatever the axis names are, so the renderer must read THOSE keys,
  // not assume 'x'/'y'. Dimension = first of x/category/region, else the first
  // axis; measures = the rest, in order.
  const axisKeys = Object.keys(fieldMap || {});
  const dimKey = ['x', 'category', 'region'].find((k) => k in (fieldMap || {})) || axisKeys[0] || 'x';
  const measureKeys = axisKeys.filter((k) => k !== dimKey);

  // The design layer (theme) or an explicit `set_property("colors", …)` supplies
  // the AUTHORITATIVE series palette. The renderer no longer imposes its own hues
  // when the design provided some — it only falls back to the neutral last-resort
  // palette when nothing was supplied. `seriesColors` (a per-series override) still
  // wins for a specific series.
  const themePalette = Array.isArray(properties.colors) && (properties.colors as string[]).length
    ? (properties.colors as string[]) : null;
  const seriesHue = (s: string, i: number) =>
    seriesColors[s] || (themePalette ? themePalette[i % themePalette.length] : FALLBACK_PALETTE[i % FALLBACK_PALETTE.length]);

  if (['grouped_bar', 'stacked_bar', 'stacked_area'].includes(t)) {
    // The proxy PIVOTS the breakdown dimension into wide rows (one column per
    // distinct breakdown value); the series are the row columns other than the
    // dimension. Derive them from the data so one bar/area is drawn per series.
    const seen = new Set<string>();
    for (const r of rows) for (const k of Object.keys(r)) if (k !== dimKey) seen.add(k);
    const rawKeys = [...seen];
    // A genuine pivot already names its columns after the category's own
    // values ("APAC", "EMEA") — never a fieldMap key, nothing to translate.
    // But a tile with no pivot (y plus a second measure such as color_by used
    // as a plain count) still lands here with its rows keyed by the SLOT name
    // itself, and the seed draws `series` verbatim as both the data key and
    // the legend text — so the legend read "y" / "color_by" on a live board
    // instead of "Story Points" / "Issue Type". Relabel only keys that are
    // still a raw fieldMap slot; a real pivoted category name is never one.
    const displayName = (k: string) => (fieldMap && k in fieldMap ? (fieldMap[k]?.label || k) : k);
    const ms = rawKeys.map(displayName);
    // ⛔ ADDED, NEVER MOVED (AG-2409). The first version of this REPLACED the
    // slot key with its label, and the label is only what the SEED needs —
    // `series` doubles as its data key. A bespoke generated component reads
    // `d[yKey]`, and this branch states no `yKey`, so its default `'y'` found
    // a key that had just been renamed away: every value came back undefined
    // and the tile drew zeros over rows that hold 36, 392 and 829. Measured
    // live on "Open issues by assignee". Both spellings now sit on the row, so
    // whichever a component reaches for is there.
    const renamed = ms.some((label, i) => label !== rawKeys[i]);
    const data = renamed
      ? rows.map((r) => {
          const row = r as Row;
          const out: Row = { ...row };
          for (const k of rawKeys) {
            const label = displayName(k);
            if (label !== k && k in row) out[label] = row[k];
          }
          return out;
        })
      : rows;
    const msColors = ms.map(seriesHue);
    return { ...base, data, xKey: dimKey, series: ms, colors: msColors };
  }

  // cartesian default (bar/column/line/area/spline/step/lollipop/waterfall/dot/density)
  const yKey = ('y' in (fieldMap || {})) ? 'y' : (measureKeys[0] || 'y');
  const y2Key = ('y2' in (fieldMap || {})) ? 'y2' : (measureKeys[1] || 'y2');
  const series = measureKeys.length ? measureKeys : Object.keys(fieldMap || {}).filter((a) => a !== dimKey);
  // Per-series colors → a `colors` array aligned to the series order, for any
  // component that accepts one. The design's palette (theme/explicit) is
  // authoritative; the neutral fallback is used only when none was supplied.
  let colors = series.map(seriesHue);
  // PER-CATEGORY COLOUR for single-measure bar/column/lollipop: when the design
  // asked for a categorical look (colorScheme 'categorical' or an explicit multi-
  // hue `colors` palette), each CATEGORY (bar) should get its own hue — the seed
  // cycles `colors[i % len]` per bar, so it needs the FULL palette. Without this a
  // lone measure collapses to one series colour and every bar renders one hue.
  const perCategoryType = ['bar', 'column', 'lollipop'].includes(t);
  const wantsCategorical = properties.colorScheme === 'categorical' || (!!themePalette && themePalette.length > 1);
  if (perCategoryType && series.length <= 1 && wantsCategorical) {
    colors = themePalette && themePalette.length ? themePalette : FALLBACK_PALETTE;
  }
  // Bridge: components that read a single `color` prop (bar/line/area/column)
  // should reflect the chosen colour. Precedence: an explicit per-series override,
  // then an explicit single `color`, then the design palette's first hue, then the
  // floor's — never '' (the seeds resolve '' to inherited text colour, D1). Don't
  // pin a single accent when we're colouring per-category (that would collapse the
  // seed back to one hue). One hue for the bars of a single measure is a deliberate
  // default (Tableau's own), not an omission: a bar chart is not a part-to-whole.
  const color = (perCategoryType && series.length <= 1 && wantsCategorical)
    ? ''
    : (seriesColors[series[0]] || (properties.color as string) || (themePalette ? themePalette[0] : '') || FALLBACK_PALETTE[0]);
  // Friendly legend labels for the y (and dual-axis y2) series: prefer the axis
  // label, then a typed yAxisLabel, else fall back to the key.
  const seriesLabel = (fieldMap?.[yKey] as AxisConfig | undefined)?.label || (properties.yAxisLabel as string) || '';
  const y2Label = (fieldMap?.[y2Key] as AxisConfig | undefined)?.label || (properties.y2AxisLabel as string) || '';
  // Pass the RESOLVED keys so components read the right row columns regardless of
  // how the axes were named. #4: merge trend-analysis overlays (moving avg,
  // peak/low, anomalies) when the tile opts into `trend` — on top of any overlays
  // the caller already passed (trend line / reference / band).
  const trend = trendOverlays(rows, yKey, properties);
  const benchmark = benchmarkOverlays(rows, yKey, properties);
  const extra = [...trend, ...benchmark];
  const mergedAll = extra.length ? [...(Array.isArray(base.overlays) ? base.overlays : []), ...extra] : base.overlays;
  const mergedOverlays = overlayColours(mergedAll, color, themePalette);
  return { ...base, xKey: dimKey, yKey, y2Key, series, colors, color, seriesLabel, y2Label, overlays: mergedOverlays };
}
